'use client';

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@coas/ui/dialog';
import { cn } from '@coas/ui/lib';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { api } from 'trpc/react';
import { useInvalidateAll } from '~/features/Cache';
import { Spinner } from '~/shared/ui/spinner';
import type { ReorderContext } from '../model/types';

type Props = {
    products: ReadonlyArray<{ id: string }>;
    context: ReorderContext;
    open: boolean;
    onOpenChange: (open: boolean, clear?: boolean) => void;
};

export const ProductSetPositionDialog: React.FC<Props> = ({ products, context, open, onOpenChange }) => {
    const productIds = products.map(p => p.id);

    const {
        register,
        handleSubmit,
        reset,
        clearErrors,
        formState: { errors },
    } = useForm<{ startIndex: number }>({
        defaultValues: { startIndex: 1 },
        mode: 'onTouched',
    });

    const { mutate: invalidateAll } = useInvalidateAll();

    const {
        data: maxOrd,
        isLoading: isMaxLoading,
        isError: isMaxError,
    } = api.product.getMaxOrd.useQuery(
        {
            productIds,
            categoryId: context.type === 'category' ? context.id : undefined,
            listId: context.type === 'list' ? context.id : undefined,
        },
        { enabled: open && !!context.id, refetchOnWindowFocus: false },
    );

    const maxPosition = typeof maxOrd === 'number' && Number.isFinite(maxOrd) ? maxOrd : 1;

    const { mutate, isPending } = api.product.setAtPosition.useMutation({
        onMutate: () => {
            toast.loading('Обновляем порядок...', { id: 'reorder' });
        },
        onSuccess: () => {
            invalidateAll();
            onOpenChange(false, true);
            toast.success('Порядок обновлён', { id: 'reorder' });
        },
        onError: () => {
            toast.error('Не удалось обновить порядок', { id: 'reorder' });
        },
    });

    useEffect(() => {
        if (!open) return;
        reset({ startIndex: 1 });
        clearErrors();
    }, [open, reset, clearErrors]);

    const blockCloseIfLoading = (e: Event) => {
        if (isPending) e.preventDefault();
    };

    return (
        <Dialog
            open={open || isPending}
            onOpenChange={next => {
                if (isPending) return;
                onOpenChange(next);
            }}
        >
            <DialogContent
                className={cn(
                    'z-[100] w-[min(92vw,580px)] rounded-12 border border-neutral-200 bg-neutral-50 text-neutral-950 shadow-lg outline-none',
                    'max-h-[min(80vh,calc(var(--visual-viewport-height)-2rem))] overflow-hidden',
                    'p-0',
                    '[&>button]:right-4 [&>button]:top-4 md:[&>button]:right-6 md:[&>button]:top-6',
                    '[&>button]:z-10',
                )}
                onEscapeKeyDown={blockCloseIfLoading}
                onPointerDownOutside={blockCloseIfLoading}
                onInteractOutside={blockCloseIfLoading}
            >
                <div className='flex max-h-full flex-col gap-4 px-4 py-4 md:px-6 md:py-6'>
                    <DialogHeader className='text-left pr-10'>
                        <div className='flex flex-col gap-2'>
                            <DialogTitle className='text-18 font-600 text-neutral-950 md:text-22'>
                                Установить позицию {context.name ? `в «${context.name}»` : ''}
                            </DialogTitle>

                            <DialogDescription className='text-left text-18 leading-snug text-neutral-500'>
                                Задайте стартовую позицию для выбранных товаров
                            </DialogDescription>
                        </div>
                    </DialogHeader>

                    <form
                        onSubmit={handleSubmit(data => {
                            const base = { start: data.startIndex, productIds };

                            if (context.type === 'category') {
                                mutate({ ...base, categoryId: context.id });
                            } else {
                                mutate({ ...base, listId: context.id });
                            }
                        })}
                        className='flex flex-col gap-4'
                    >
                        <label className='flex flex-col gap-1 text-16'>
                            Позиция в списке:
                            <input
                                type='number'
                                min={1}
                                max={maxPosition}
                                {...register('startIndex', {
                                    valueAsNumber: true,
                                    min: { value: 1, message: 'Минимальная позиция — 1' },
                                    max: { value: maxPosition, message: `Максимальная позиция — ${maxPosition}` },
                                })}
                                className={cn(
                                    'w-full rounded-8 border border-neutral-300 bg-white px-2 py-1 text-16 text-neutral-900',
                                    'outline-none ring-0 focus:border-neutral-800',
                                )}
                            />
                            <span className='text-12 text-neutral-500'>
                                {isMaxLoading
                                    ? 'Считаем доступные позиции…'
                                    : isMaxError
                                      ? 'Не удалось получить максимум, попробуйте позже'
                                      : `Укажите от 1 до ${maxPosition}`}
                            </span>
                            {errors.startIndex && (
                                <span className='text-12 text-red-600'>{errors.startIndex.message}</span>
                            )}
                        </label>

                        <DialogFooter className='p-0'>
                            <div className='flex flex-row items-center justify-end gap-2 sm:gap-3'>
                                <DialogClose asChild>
                                    <button
                                        type='button'
                                        onClick={() => onOpenChange(false)}
                                        disabled={isPending}
                                        className={cn(
                                            'h-9 px-3 text-13 md:px-4 md:text-14',
                                            'rounded-8 font-500 shrink-0',
                                            'bg-neutral-200 text-neutral-900',
                                            'transition-colors duration-150 hover:bg-neutral-300',
                                            'disabled:pointer-events-none disabled:opacity-60',
                                        )}
                                    >
                                        Отмена
                                    </button>
                                </DialogClose>

                                <button
                                    type='submit'
                                    disabled={isPending || productIds.length === 0 || isMaxLoading}
                                    className={cn(
                                        'h-9 px-3 text-13 md:px-4 md:text-14',
                                        'rounded-8 font-700 shrink-0',
                                        'bg-neutral-950 text-white',
                                        'transition-opacity duration-150',
                                        'disabled:pointer-events-none disabled:opacity-60',
                                        isPending && 'animate-pulse',
                                    )}
                                >
                                    Установить
                                </button>
                            </div>
                        </DialogFooter>
                    </form>
                </div>

                {isPending && (
                    <div className='absolute inset-0 z-50 flex items-center justify-center rounded-12 bg-neutral-50/70'>
                        <Spinner className='size-6' />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
